import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  CreditCard,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar
} from 'lucide-react'
import { adminApi, AdminSubscription, CreateSubscriptionRequest, UpdateSubscriptionRequest } from '@/api/admin'
import type { SubscriptionType, SubscriptionStatus } from '@/types/api'

/** UTC ISO 문자열을 로컬 datetime-local 입력 포맷(YYYY-MM-DDTHH:mm)으로 변환 */
function toLocalDatetime(isoString: string | null | undefined): string {
  if (!isoString) return ''
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return isoString.slice(0, 16)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const SUBSCRIPTION_TYPES: SubscriptionType[] = ['one_time', 'monthly', 'quarterly', 'yearly', 'trial']
const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = ['active', 'paused', 'expired', 'cancelled', 'suspended']

const TYPE_LABELS: Record<SubscriptionType, string> = {
  one_time: '일회성',
  monthly: '월간',
  quarterly: '분기',
  yearly: '연간',
  trial: '체험'
}

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: '활성',
  paused: '일시정지',
  expired: '만료',
  cancelled: '취소',
  suspended: '정지'
}

const STATUS_COLORS: Record<SubscriptionStatus, string> = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-red-100 text-red-700',
  suspended: 'bg-orange-100 text-orange-700'
}

interface SubscriptionModalProps {
  subscription?: AdminSubscription | null
  onClose: () => void
  onSave: (data: CreateSubscriptionRequest | UpdateSubscriptionRequest) => void
  isLoading: boolean
}

function SubscriptionModal({ subscription, onClose, onSave, isLoading }: SubscriptionModalProps) {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<CreateSubscriptionRequest>({
    orderId: subscription?.orderId || 0,
    productId: subscription?.productId || 0,
    pricingId: subscription?.pricingId || 0,
    ownerUserId: subscription?.ownerUserId || 0,
    subscriptionType: subscription?.subscriptionType || 'monthly',
    status: subscription?.status || 'active',
    maxSeats: subscription?.maxSeats || 1,
    validFrom: toLocalDatetime(subscription?.validFrom) || toLocalDatetime(new Date().toISOString()),
    validUntil: toLocalDatetime(subscription?.validUntil),
    autoRenew: subscription?.autoRenew ?? true
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (subscription) {
      const updateData: UpdateSubscriptionRequest = {
        status: formData.status,
        maxSeats: formData.maxSeats,
        validUntil: formData.validUntil || undefined,
        autoRenew: formData.autoRenew
      }
      onSave(updateData)
    } else {
      onSave(formData)
    }
  }

  if (!subscription) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900">새로운 구독(License) 생성</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-700 leading-relaxed">
              구독 생성은 <span className="font-semibold">[상품관리]</span> 페이지에서 진행 할 수 있습니다.
              <br />
              지정된 상품의 공개된 조직 옆에 <span className="font-semibold">&lt;직접 구매 해주기&gt;</span> 버튼을 눌러주세요.
            </p>
          </div>

          <div className="p-6 pt-0 flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">
              닫기
            </button>
            <button
              type="button"
              onClick={() => {
                onClose()
                navigate('/products')
              }}
              className="btn-primary btn-md flex-1"
            >
              상품관리로 이동
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            구독 수정
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!subscription && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">주문 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.orderId}
                    onChange={(e) => setFormData({ ...formData, orderId: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <label className="label">상품 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.productId}
                    onChange={(e) => setFormData({ ...formData, productId: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">가격정책 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.pricingId}
                    onChange={(e) => setFormData({ ...formData, pricingId: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <label className="label">소유자 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.ownerUserId}
                    onChange={(e) => setFormData({ ...formData, ownerUserId: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">구독 유형 *</label>
              <select
                className="input"
                value={formData.subscriptionType}
                onChange={(e) => setFormData({ ...formData, subscriptionType: e.target.value as SubscriptionType })}
                disabled={!!subscription}
              >
                {SUBSCRIPTION_TYPES.map((type) => (
                  <option key={type} value={type}>{TYPE_LABELS[type]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">상태 *</label>
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as SubscriptionStatus })}
              >
                {SUBSCRIPTION_STATUSES.map((status) => (
                  <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label">최대 좌석 수</label>
            <input
              type="number"
              className="input"
              min={1}
              value={formData.maxSeats || ''}
              onChange={(e) => setFormData({ ...formData, maxSeats: parseInt(e.target.value) || 1 })}
              onFocus={(e) => e.target.select()}
              placeholder="1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">시작일 *</label>
              <input
                type="datetime-local"
                className="input"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                required
                disabled={!!subscription}
              />
            </div>
            <div>
              <label className="label">종료일</label>
              <input
                type="datetime-local"
                className="input"
                value={formData.validUntil || ''}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.autoRenew}
                onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">자동 갱신</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">
              취소
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary btn-md flex-1">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : subscription ? '수정' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

interface DeleteSubscriptionConfirmModalProps {
  subscription: AdminSubscription
  onClose: () => void
  onConfirm: () => void
  isDeleting: boolean
}

function DeleteSubscriptionConfirmModal({
  subscription,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteSubscriptionConfirmModalProps) {
  const now = new Date()
  const hasRemainingValidity = !!subscription.validUntil && new Date(subscription.validUntil) > now
  const hasActiveLearners = subscription.usedSeats > 0

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">구독 삭제 확인</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" disabled={isDeleting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            삭제 전에 아래 항목을 확인해주세요. 그래도 삭제할 경우 되돌릴 수 없습니다.
          </p>

          <div className="space-y-2 text-sm">
            <div className={`p-3 rounded-lg border ${hasRemainingValidity ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
              만기일이 아직 남아있는 경우: {hasRemainingValidity ? `예 (${new Date(subscription.validUntil!).toLocaleDateString('ko-KR')}까지)` : '아니오'}
            </div>

            <div className={`p-3 rounded-lg border ${hasActiveLearners ? 'bg-red-50 border-red-200 text-red-700 font-medium' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
              현재 학습자가 있는 경우: {hasActiveLearners ? `예 (${subscription.usedSeats}명 사용 중)` : '아니오'}
            </div>
          </div>
        </div>

        <div className="p-6 pt-0 flex gap-3">
          <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1" disabled={isDeleting}>
            취소
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            disabled={isDeleting}
          >
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : '그래도 삭제'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

interface PricingDetailModalProps {
  productId: number
  pricingId: number
  pricingName?: string | null
  onClose: () => void
}

function PricingDetailModal({ productId, pricingId, pricingName, onClose }: PricingDetailModalProps) {
  const { data: pricings = [], isLoading } = useQuery({
    queryKey: ['admin', 'products', productId, 'pricings', pricingId],
    queryFn: () => adminApi.getProductPricings(productId),
    enabled: !!productId && !!pricingId,
  })

  const pricing = pricings.find((p) => Number(p.id) === Number(pricingId))
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(amount)

  const PRICING_TYPE_LABELS: Record<string, string> = {
    one_time: '일회성',
    subscription: '구독',
  }

  // billingPeriodLabel은 서버에서 제공

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">가격정책 상세</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>가격정책 정보를 불러오는 중...</span>
            </div>
          ) : !pricing ? (
            <div className="space-y-2 text-sm">
              <p className="text-slate-700 font-medium">{pricingName || `가격정책 #${pricingId}`}</p>
              <p className="text-slate-600">
                이 구독에 연결된 가격정책은 현재 상품 가격 목록에서 조회되지 않습니다.
              </p>
              <p className="text-slate-500">
                (상품 ID: {productId}, 가격정책 ID: {pricingId})
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="col-span-2">
                <label className="text-slate-500">옵션명</label>
                <p className="text-slate-900 font-medium">{pricing.name}</p>
              </div>
              <div>
                <label className="text-slate-500">가격 유형</label>
                <p className="text-slate-900">{PRICING_TYPE_LABELS[pricing.pricingType] ?? pricing.pricingType}</p>
              </div>
              <div>
                <label className="text-slate-500">결제 주기</label>
                <p className="text-slate-900">{pricing.billingPeriodLabel}</p>
              </div>
              <div>
                <label className="text-slate-500">판매가</label>
                <p className="text-slate-900 font-medium">{pricing.isNegotiable ? '별도 협의' : formatCurrency(pricing.price)}</p>
              </div>
              <div>
                <label className="text-slate-500">최대 좌석</label>
                <p className="text-slate-900">{pricing.maxSeats}</p>
              </div>
              <div>
                <label className="text-slate-500">할인율</label>
                <p className="text-slate-900">{pricing.discountPercentage ? `${pricing.discountPercentage}%` : '-'}</p>
              </div>
              <div>
                <label className="text-slate-500">무료 체험</label>
                <p className="text-slate-900">{pricing.hasTrial ? `${pricing.trialDays}일` : '없음'}</p>
              </div>
              <div>
                <label className="text-slate-500">상태</label>
                <p className="text-slate-900">{pricing.isActive ? '활성' : '비활성'}</p>
              </div>
              <div>
                <label className="text-slate-500">기본 옵션</label>
                <p className="text-slate-900">{pricing.isDefault ? '예' : '아니오'}</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 pt-0">
          <button onClick={onClose} className="btn-secondary btn-md w-full">
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export function SubscriptionManagement() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [filterStatus, setFilterStatus] = useState<SubscriptionStatus | ''>('')
  const [filterType, setFilterType] = useState<SubscriptionType | ''>('')
  const [modalSubscription, setModalSubscription] = useState<AdminSubscription | null | undefined>(undefined)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [deleteTarget, setDeleteTarget] = useState<AdminSubscription | null>(null)
  const [pricingDetailTarget, setPricingDetailTarget] = useState<{ productId: number; pricingId: number; pricingName?: string | null } | null>(null)

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'subscriptions', { page, filterStatus, filterType }],
    queryFn: () => adminApi.getSubscriptions({
      page,
      size: 20,
      status: filterStatus || undefined,
      subscriptionType: filterType || undefined
    }),
    staleTime: 30 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: adminApi.createSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
      setModalSubscription(undefined)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSubscriptionRequest }) =>
      adminApi.updateSubscription(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
      setModalSubscription(undefined)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteSubscription,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
  })

  const handleSave = (formData: CreateSubscriptionRequest | UpdateSubscriptionRequest) => {
    if (modalSubscription) {
      updateMutation.mutate({ id: modalSubscription.id, data: formData as UpdateSubscriptionRequest })
    } else {
      createMutation.mutate(formData as CreateSubscriptionRequest)
    }
  }

  const handleDelete = (subscription: AdminSubscription) => {
    setDeleteTarget(subscription)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">구독 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-green-600" />
            구독 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}건의 구독</p>
        </div>
        <button
          onClick={() => setModalSubscription(null)}
          className="btn-primary btn-md inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          새 구독
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            className="input w-40"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as SubscriptionStatus | ''); setPage(0) }}
          >
            <option value="">모든 상태</option>
            {SUBSCRIPTION_STATUSES.map((status) => (
              <option key={status} value={status}>{STATUS_LABELS[status]}</option>
            ))}
          </select>
          <select
            className="input w-40"
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value as SubscriptionType | ''); setPage(0) }}
          >
            <option value="">모든 유형</option>
            {SUBSCRIPTION_TYPES.map((type) => (
              <option key={type} value={type}>{TYPE_LABELS[type]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-10 px-4 py-4"></th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상품</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">소유자</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700 whitespace-nowrap">유형</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700 whitespace-nowrap">상태</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">좌석</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">유효기간</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((sub) => {
                const isExpanded = expandedRows.has(sub.id)
                return (
                  <>
                    <tr key={sub.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <button
                          onClick={() => toggleRow(sub.id)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{sub.id}</td>
                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">
                        {sub.productName || `#${sub.productId}`}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{sub.ownerFullName}</p>
                          <p className="text-xs text-slate-500">{sub.ownerEmail}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 whitespace-nowrap">
                          {TYPE_LABELS[sub.subscriptionType]}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[sub.status]}`}>
                          {STATUS_LABELS[sub.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {sub.usedSeats} / {sub.maxSeats}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <div className="flex items-start gap-2">
                          <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div className="leading-5">
                            <p>{new Date(sub.validFrom).toLocaleDateString('ko-KR')}</p>
                            <p>{sub.validUntil ? new Date(sub.validUntil).toLocaleDateString('ko-KR') : '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setModalSubscription(sub)}
                            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                            title="수정"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(sub)}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                            title="삭제"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${sub.id}-details`} className="bg-slate-50">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="ml-16 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">상품:</span>
                              <span className="ml-2">{sub.productName || `#${sub.productId}`}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">가격정책:</span>
                              <button
                                onClick={() =>
                                  setPricingDetailTarget({
                                    productId: sub.productId,
                                    pricingId: sub.pricingId,
                                    pricingName: sub.pricingName,
                                  })
                                }
                                className="ml-2 text-blue-600 hover:text-blue-700 hover:underline font-medium"
                              >
                                {sub.pricingName || `#${sub.pricingId}`}
                              </button>
                            </div>
                            <div>
                              <span className="text-slate-500">주문 ID:</span>
                              <span className="ml-2 font-mono">{sub.orderId}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">자동 갱신:</span>
                              <span className={`ml-2 ${sub.autoRenew ? 'text-green-600' : 'text-slate-500'}`}>
                                {sub.autoRenew ? 'Yes' : 'No'}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-500">다음 결제일:</span>
                              <span className="ml-2">
                                {sub.nextBillingDate ? new Date(sub.nextBillingDate).toLocaleDateString('ko-KR') : '-'}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-500">활성화일:</span>
                              <span className="ml-2">
                                {sub.activatedAt ? new Date(sub.activatedAt).toLocaleDateString('ko-KR') : '-'}
                              </span>
                            </div>
                            {sub.cancelledAt && (
                              <>
                                <div>
                                  <span className="text-slate-500">취소일:</span>
                                  <span className="ml-2 text-red-600">
                                    {new Date(sub.cancelledAt).toLocaleDateString('ko-KR')}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-slate-500">취소 사유:</span>
                                  <span className="ml-2">{sub.cancellationReason || '-'}</span>
                                </div>
                              </>
                            )}
                            <div>
                              <span className="text-slate-500">생성일:</span>
                              <span className="ml-2">{new Date(sub.createdAt).toLocaleString('ko-KR')}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">수정일:</span>
                              <span className="ml-2">{new Date(sub.updatedAt).toLocaleString('ko-KR')}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {data?.content.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">등록된 구독이 없습니다</p>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalSubscription !== undefined && (
        <SubscriptionModal
          subscription={modalSubscription}
          onClose={() => setModalSubscription(undefined)}
          onSave={handleSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {pricingDetailTarget && (
        <PricingDetailModal
          productId={pricingDetailTarget.productId}
          pricingId={pricingDetailTarget.pricingId}
          pricingName={pricingDetailTarget.pricingName}
          onClose={() => setPricingDetailTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteSubscriptionConfirmModal
          subscription={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleteTarget.id, {
              onSuccess: () => setDeleteTarget(null),
              onError: () => setDeleteTarget(null),
            })
          }}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  )
}





















